import React from 'react';
import BaseModal from './BaseModal';
import { conditionOf } from './formConditions';
import { flattenFields, flattenSections, sectionFields, sectionTitle } from './formSections';

// What a section is about to take with it, in words: the questions anywhere
// inside it, and the subsections they are filed under.
const describeContents = (questions, sections) => {
  const parts = [`${questions} question${questions === 1 ? '' : 's'}`];
  if (sections > 0) parts.push(`${sections} subsection${sections === 1 ? '' : 's'}`);
  return parts.join(' in ');
};

// Deleting a section asks two different questions at once — is the grouping
// wrong, or is the whole chapter wrong? — so the choice is made in the dialog
// rather than guessed at. Both answers are buttons in the body; Cancel is the
// footer, and the one the keyboard lands on.
const SectionDeleteModal = ({ open, section, onChoose, onCancel }) => {
  if (!section) return null;
  const entries = sectionFields(section);
  const questions = flattenFields(entries).length;
  const subsections = flattenSections(entries).length;
  const empty = entries.length === 0;
  const contents = describeContents(questions, subsections);

  const choiceClass =
    'w-full text-left bg-gray-700/40 border rounded-lg px-4 py-3 transition-colors duration-200';

  return (
    <BaseModal
      open={open}
      titleId="section-delete-title"
      title={`Delete “${sectionTitle(section)}”`}
      message={
        empty
          ? 'This section is empty.'
          : `This section holds ${contents}. Choose what happens to them.`
      }
      onSubmit={(e) => e.preventDefault()}
      onCancel={onCancel}
      showConfirm={false}
      danger
    >
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => onChoose('all')}
          className={`${choiceClass} border-red-500/40 hover:bg-red-500/20 hover:border-red-400`}
        >
          <span className="block font-semibold text-white">Delete everything</span>
          <span className="block text-sm text-gray-400">
            {empty
              ? 'Removes the section.'
              : `Removes the section and the ${contents} inside it.`}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onChoose('sectionOnly')}
          className={`${choiceClass} border-purple-500/40 hover:bg-purple-500/20 hover:border-purple-400`}
        >
          <span className="block font-semibold text-white">Delete section only</span>
          <span className="block text-sm text-gray-400">
            {empty
              ? 'Removes the section.'
              : 'Removes the heading and leaves everything inside it where it is on the form.'}
            {/* The condition belongs to the section, so it can't stay behind
                on what the section held. */}
            {!empty && conditionOf(section)
              && ' The section’s condition goes too, so its questions no longer wait on that answer.'}
          </span>
        </button>
      </div>
    </BaseModal>
  );
};

export default SectionDeleteModal;
